/* istanbul ignore file */
import { FormSection, FormRow, BigFormRow } from 'framework/ui/layout';
import { DateInput, TextArea, TextInput, ClearAfterUnmountWrapper, useInputHelper } from 'framework/form';
import { EXPENSE_SUBTYPES_ALIASES, EXPENSE_TYPES } from 'domain/shared/expense';
import useLedgerService from 'domain/finance/ledger/useLedgerService';
import ExpenseTypeDropdown from 'domain/finance/expenses/shared/input/ExpenseTypeDropdown';
import ExpenseSubTypeDropdown from 'domain/finance/expenses/shared/input/ExpenseSubTypeDropdown';
import ExpenseTaxSubTypeDropdown from 'domain/finance/expenses/shared/input/ExpenseTaxSubTypeDropdown';
import { EmployeeInput, buildEmployeeAutoSuggestLabel } from 'domain/shared/employee';
import { snakeCase } from 'lodash';
import ExpenseOfficeDropdown from 'domain/finance/expenses/shared/input/ExpenseOfficeDropdown';
import { InvoiceTotalInput } from 'domain/shared/core/invoice';
import ExpenseCurrencyDropdown from 'domain/finance/expenses/shared/input/ExpenseCurrencyDropdown';
import ExpensesLeaveDropdown from 'domain/finance/expenses/shared/input/ExpensesLeaveDropdown';
import { AccountInput } from 'domain/shared/ledger';
import { ExpenseProjectDropdown } from 'domain/shared/project';
import ExpenseStartEndDateInput from 'domain/finance/expenses/shared/input/ExpenseStartEndDateInput';
import { BudgetCategoryDropdown } from 'domain/shared/budget';
import { isBudgetCategoryRequired } from 'domain/shared/budget';

const isPerformanceBonus = (data: any) => data.type === EXPENSE_TYPES.PERFORMANCE_BASED_BONUS;

const isAccountRequired = (data: any): boolean =>
  data.type === EXPENSE_TYPES.DEDUCTION ||
  data.subtype === EXPENSE_SUBTYPES_ALIASES[EXPENSE_TYPES.ONE_TIME_PAYMENT].REIMBURSEMENTS;

const isProjectVisible = (data: any): boolean =>
  data.subtype === EXPENSE_SUBTYPES_ALIASES[EXPENSE_TYPES.ONE_TIME_PAYMENT].SALES_BONUSES_AND_COMMISSIONS ||
  data.subtype === EXPENSE_SUBTYPES_ALIASES[EXPENSE_TYPES.ONE_TIME_PAYMENT].ACCOUNT_MANAGER_BONUSES_AND_COMMISSIONS ||
  data.subtype === EXPENSE_SUBTYPES_ALIASES[EXPENSE_TYPES.ONE_TIME_PAYMENT].CUSTOMER_EXCELLENCE_BONUS ||
  (data.subtype === EXPENSE_SUBTYPES_ALIASES[EXPENSE_TYPES.ONE_TIME_PAYMENT].REIMBURSEMENTS &&
    data.transferAccount === 'Expenses/Delivery/Service delivery/USD');

type ExpenseFormProps = {
  disabledOffice?: boolean;
  isNeedToUploadOfficeByEmployee?: boolean;
};

const ExpenseForm = ({ disabledOffice, isNeedToUploadOfficeByEmployee }: ExpenseFormProps) => {
  const { searchEmployeeExpensesAccount } = useLedgerService();
  const { getValue: getAccount } = useInputHelper<string>({ source: 'transferAccount' });
  const transferAccount = getAccount();
  const { getValue: getInvoiceDate } = useInputHelper<string>({ source: 'registrationDate' });
  const registrationDate = getInvoiceDate();

  return (
    <FormSection>
      <FormRow>
        <ExpenseTypeDropdown source="type" label="Type" />
        <ClearAfterUnmountWrapper
          isVisible={(data: any) =>
            [
              EXPENSE_TYPES.LEAVE_COMPENSATION,
              EXPENSE_TYPES.ONE_TIME_PAYMENT,
              EXPENSE_TYPES.BENEFITS_EXPENSE,
              EXPENSE_TYPES.DEDUCTION,
            ].includes(data.type)
          }
        >
          <ExpenseSubTypeDropdown source="subtype" typeSource="type" />
        </ClearAfterUnmountWrapper>
        <ClearAfterUnmountWrapper isVisible={(data: any) => EXPENSE_TYPES.TAX === data.type}>
          <ExpenseTaxSubTypeDropdown source="subtype" officeSource="office" />
        </ClearAfterUnmountWrapper>
        <ClearAfterUnmountWrapper isVisible={(data: any) => EXPENSE_TYPES.TAX !== data.type}>
          <EmployeeInput
            source="employeeAlias"
            label="Worker"
            isInitialNameLoad
            mapResults={(employee: any) => ({
              id: employee.alias,
              text: buildEmployeeAutoSuggestLabel(employee),
              office: employee.office ? snakeCase(employee.office).toUpperCase() : null,
            })}
          />
        </ClearAfterUnmountWrapper>
        <ExpenseOfficeDropdown
          employeeSource="employeeAlias"
          source="office"
          placeholder=""
          isNeedToUploadOfficeByEmployee={isNeedToUploadOfficeByEmployee}
          disabled={(data: any) => data.type === EXPENSE_TYPES.TAX || !!disabledOffice}
        />
      </FormRow>

      <FormRow>
        <TextInput source="title" label="Title" />
        <InvoiceTotalInput source="compensation.amount" currencySource="compensation.currency" label="Monetary value" />
        <ExpenseCurrencyDropdown
          disabled={disabledOffice}
          source="compensation.currency"
          label="Currency"
          typeSource="subtype"
        />
        <ClearAfterUnmountWrapper isVisible={(data: any) => data.type === EXPENSE_TYPES.LEAVE_COMPENSATION}>
          <ExpensesLeaveDropdown
            source="leaveId"
            label="Leave"
            employeeSource="employeeAlias"
            typeSource="subtype"
            checkDepending
          />
        </ClearAfterUnmountWrapper>
        <ClearAfterUnmountWrapper
          isVisible={(data: any) =>
            data.type === EXPENSE_TYPES.DEDUCTION ||
            data.subtype === EXPENSE_SUBTYPES_ALIASES[EXPENSE_TYPES.ONE_TIME_PAYMENT].REIMBURSEMENTS
          }
        >
          <AccountInput
            source="transferAccount"
            label="Account to debit"
            loadDataMethod={searchEmployeeExpensesAccount}
            cacheKey="employee-expenses-accounts"
          />
        </ClearAfterUnmountWrapper>
        <ClearAfterUnmountWrapper isVisible={isProjectVisible}>
          <ExpenseProjectDropdown source="project.projectId" label="Project" />
        </ClearAfterUnmountWrapper>
      </FormRow>

      <FormRow>
        <DateInput source="registrationDate" />
        <ClearAfterUnmountWrapper isVisible={isPerformanceBonus}>
          <ExpenseStartEndDateInput source="startDate" />
        </ClearAfterUnmountWrapper>
        <ClearAfterUnmountWrapper isVisible={isPerformanceBonus}>
          <ExpenseStartEndDateInput source="endDate" isEndDate />
        </ClearAfterUnmountWrapper>
        <ClearAfterUnmountWrapper
          isVisible={(data: any) => data.registrationDate && data.transferAccount && isAccountRequired(data)}
        >
          <BudgetCategoryDropdown
            disabled={!isBudgetCategoryRequired(transferAccount)}
            source="budgetCategory"
            label="Budget category"
            dateValue={registrationDate}
            accountIdValue={transferAccount}
          />
        </ClearAfterUnmountWrapper>
      </FormRow>

      <BigFormRow>
        <TextArea source="description" />
      </BigFormRow>
    </FormSection>
  );
};

export default ExpenseForm;
